import { useState } from 'react'
import {
  useArrangementStore,
  type SegmentBars,
} from '../../state/arrangementStore'
import { loadSegmentIntoEditor } from '../../state/loadSegment'
import { ParentGate } from '../ParentGate/ParentGate'
import './ArrangementStrip.css'

const BAR_CHOICES: SegmentBars[] = [1, 2, 4]

export function ArrangementStrip() {
  const order = useArrangementStore((s) => s.order)
  const segments = useArrangementStore((s) => s.segments)
  const selectedSegmentId = useArrangementStore((s) => s.selectedSegmentId)
  const activeSegmentId = useArrangementStore((s) => s.activeSegmentId)
  const arrangementPlayMode = useArrangementStore((s) => s.arrangementPlayMode)
  const expanded = useArrangementStore((s) => s.expanded)
  const moveSegment = useArrangementStore((s) => s.moveSegment)
  const removeSegment = useArrangementStore((s) => s.removeSegment)
  const duplicateSegment = useArrangementStore((s) => s.duplicateSegment)
  const selectSegment = useArrangementStore((s) => s.selectSegment)
  const setSegmentBars = useArrangementStore((s) => s.setSegmentBars)
  const clearArrangement = useArrangementStore((s) => s.clearArrangement)
  const setArrangementPlayMode = useArrangementStore(
    (s) => s.setArrangementPlayMode,
  )
  const setExpanded = useArrangementStore((s) => s.setExpanded)

  const [pendingRemoval, setPendingRemoval] = useState<string | null>(null)
  const [clearGate, setClearGate] = useState(false)

  const playingId = arrangementPlayMode ? activeSegmentId : null

  const confirmRemove = () => {
    if (pendingRemoval) removeSegment(pendingRemoval)
    setPendingRemoval(null)
  }

  const confirmClear = () => {
    clearArrangement()
    setClearGate(false)
  }

  if (!expanded) {
    const activeName = activeSegmentId && segments[activeSegmentId]?.name
    return (
      <button
        type="button"
        className="arrangement-handle"
        onClick={() => setExpanded(true)}
        aria-label="Show song arrangement"
        aria-expanded={false}
      >
        <span className="arrangement-handle-label">SONG</span>
        <span className="arrangement-handle-count">
          {order.length} {order.length === 1 ? 'segment' : 'segments'}
        </span>
        {arrangementPlayMode && activeName && (
          <span className="arrangement-handle-playing">▶ {activeName}</span>
        )}
        <span className="arrangement-handle-chevron" aria-hidden="true">
          ▲
        </span>
      </button>
    )
  }

  return (
    <aside className="arrangement" aria-label="Arrangement">
      <div className="arrangement-label">
        <button
          type="button"
          className="arrangement-collapse"
          onClick={() => setExpanded(false)}
          aria-label="Collapse song arrangement"
        >
          ▼
        </button>
        <span className="arrangement-label-name">SONG</span>
        <span className="arrangement-label-count">{order.length}</span>
      </div>

      <div className="arrangement-tiles" role="list">
        {order.length === 0 && (
          <span className="arrangement-empty">
            Save a segment in parent mode to start arranging.
          </span>
        )}
        {order.map((id, index) => {
          const segment = segments[id]
          if (!segment) return null
          const selected = selectedSegmentId === id
          const playing = playingId === id
          return (
            <div
              key={id}
              role="listitem"
              className={[
                'arrangement-tile',
                selected ? 'is-selected' : '',
                playing ? 'is-playing' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <button
                type="button"
                className="arrangement-tile-main"
                onClick={() => {
                  selectSegment(id)
                  loadSegmentIntoEditor(segment)
                }}
                aria-label={`Load segment ${segment.name}`}
                aria-pressed={selected}
              >
                <span className="arrangement-tile-name">{segment.name}</span>
                <span className="arrangement-tile-meta">
                  {segment.bpm} BPM · {segment.bars}×
                </span>
              </button>
              <div className="arrangement-tile-controls">
                <div className="arrangement-tile-move">
                  <button
                    type="button"
                    className="arrangement-ctl"
                    onClick={() => moveSegment(id, -1)}
                    disabled={index === 0}
                    aria-label={`Move ${segment.name} left`}
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    className="arrangement-ctl"
                    onClick={() => moveSegment(id, 1)}
                    disabled={index === order.length - 1}
                    aria-label={`Move ${segment.name} right`}
                  >
                    →
                  </button>
                </div>
                <div
                  className="arrangement-tile-bars"
                  role="group"
                  aria-label={`${segment.name} bars`}
                >
                  {BAR_CHOICES.map((bars) => (
                    <button
                      key={bars}
                      type="button"
                      className={`arrangement-bar ${segment.bars === bars ? 'is-active' : ''}`}
                      onClick={() => setSegmentBars(id, bars)}
                      aria-label={`${bars} bar${bars === 1 ? '' : 's'}`}
                      aria-pressed={segment.bars === bars}
                    >
                      ×{bars}
                    </button>
                  ))}
                </div>
                <div className="arrangement-tile-side">
                  <button
                    type="button"
                    className="arrangement-ctl"
                    onClick={() => duplicateSegment(id)}
                    aria-label={`Duplicate ${segment.name}`}
                  >
                    ⧉
                  </button>
                  <button
                    type="button"
                    className="arrangement-ctl arrangement-ctl-danger"
                    onClick={() => setPendingRemoval(id)}
                    aria-label={`Remove ${segment.name}`}
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="arrangement-aside">
        <button
          type="button"
          className={`arrangement-mode ${arrangementPlayMode ? 'is-song' : 'is-loop'}`}
          onClick={() => setArrangementPlayMode(!arrangementPlayMode)}
          disabled={order.length === 0}
          aria-label={
            arrangementPlayMode ? 'Switch to loop mode' : 'Switch to song mode'
          }
          aria-pressed={arrangementPlayMode}
        >
          {arrangementPlayMode ? 'SONG' : 'LOOP'}
        </button>
        {order.length > 0 && (
          <button
            type="button"
            className="arrangement-clear"
            onClick={() => setClearGate(true)}
            aria-label="Clear arrangement"
          >
            🧹
          </button>
        )}
      </div>

      {pendingRemoval && segments[pendingRemoval] && (
        <ParentGate
          title={`Remove ${segments[pendingRemoval].name}?`}
          onConfirm={confirmRemove}
          onCancel={() => setPendingRemoval(null)}
        />
      )}
      {clearGate && (
        <ParentGate
          title="Wipe the whole arrangement?"
          onConfirm={confirmClear}
          onCancel={() => setClearGate(false)}
        />
      )}
    </aside>
  )
}
